import { TellescopeModelFromName, useTellescopeInfiniteQuery, useTellescopeQuery } from "@tellescope/tanstack-query";
import { useEmailCompositionPanelSearchParams, usePollForInboxThread } from "../../../hooks";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "@tellescope/forms";
import { useTranslation } from "react-i18next";
import { Loader2, X } from "lucide-react";
import { Link } from "../../../../../components";
import { Badge, Button, Combobox, ComboboxOption, Input, Label } from "@tellescope/ui";
import { InboxEmailRecipientItem } from "./InboxEmailRecipientItem";
import { formatName } from "@tellescope/utils";
import { InboxEmailCompositionFooter } from "./InboxEmailCompositionFooter";
import { PAGE_HEADER_SIZE_STRING } from "../../../../../constants/header-sizing";

export const InboxEmailCompositionPanel = () => {
  const { t } = useTranslation("inbox");
  const [searchParams, setSearchParams] = useEmailCompositionPanelSearchParams();
  const [enduserSearch, setEnduserSearch] = useState("");
  const [ccEnduserSearch, setCcEnduserSearch] = useState("");
  const [selectedEnduser, setSelectedEnduser] = useState<TellescopeModelFromName<"endusers">>();
  const [ccEndusers, setCcEndusers] = useState<TellescopeModelFromName<"endusers">[]>([]);
  const [isSending, setIsSending] = useState(false);

  const isForwarding = !!searchParams.forward && !!searchParams.emailId;
  const emailToForwardQuery = useTellescopeQuery({
    model: "emails",
    method: "getOne",
    request: searchParams.emailId ?? "",
    enabled: isForwarding,
  });

  const endusersQuery = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: {
      limit: 20,
      search: enduserSearch
        ? {
            query: enduserSearch,
          }
        : undefined,
    },
    select: (data) => data.pages.flat(),
  });

  // Fetch related contacts when a recipient is selected
  const relatedContactsQuery = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: selectedEnduser?.id ?? "",
    enabled: !!selectedEnduser?.id,
    select: (data) => data?.relationships ?? [],
  });

  const ccEndusersQuery = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: {
      limit: 20,
      search: ccEnduserSearch
        ? {
            query: ccEnduserSearch,
          }
        : undefined,
    },
    select: (data) => {
      const allEndusers = data.pages.flat();
      const relationshipsMap = new Map(relatedContactsQuery.data?.map((rel) => [rel.id, rel.type]) ?? []);

      // Combine all endusers with relationship info
      const endusersWithRelationships = allEndusers.map((enduser) => ({
        enduser,
        relationshipType: relationshipsMap.get(enduser.id),
        isRelated: relationshipsMap.has(enduser.id),
      }));

      // Sort: related contacts first, then unrelated contacts
      endusersWithRelationships.sort((a, b) => {
        if (a.isRelated && !b.isRelated) return -1;
        if (!a.isRelated && b.isRelated) return 1;
        return 0;
      });

      return {
        endusers: allEndusers,
        comboboxOptions: endusersWithRelationships.map<ComboboxOption<string>>(({ enduser, relationshipType }) => ({
          value: enduser.id,
          label: formatName(enduser),
          metadata: {
            enduserId: enduser.id,
            ...(relationshipType && { relationshipType }),
          },
        })),
      };
    },
  });

  const availableEmails = useMemo(() => {
    if (!selectedEnduser) return [];

    const emails: { value: string; label: string; isPrimary: boolean }[] = [];

    if (selectedEnduser.email) {
      emails.push({
        value: selectedEnduser.email,
        label: `${selectedEnduser.email} (Primary)`,
        isPrimary: true,
      });
    }

    if (selectedEnduser.alternateEmails) {
      selectedEnduser.alternateEmails.forEach((email) => {
        emails.push({
          value: email,
          label: email,
          isPrimary: false,
        });
      });
    }

    return emails;
  }, [selectedEnduser]);

  const pollForThread = usePollForInboxThread({
    maxAttempts: 10,
    initialBackoffMs: 1000,
    onMaxAttemptsReached: () => setIsSending(false),
  });

  const form = useForm({
    fields: {
      enduserId: {
        type: "text",
        initialValue: decodeURI(searchParams.emailCompositionEnduserId ?? ""),
        required: true,
      },
      emailAddress: {
        type: "text",
        initialValue: decodeURI(searchParams.emailCompositionEmailAddress ?? ""),
        required: false,
      },
      subject: {
        type: "text",
        initialValue: isForwarding
          ? `Fwd: ${emailToForwardQuery.data?.subject ?? ""}`
          : decodeURI(searchParams.emailCompositionSubject ?? ""),
        required: true,
      },
    },
    validateOn: "blur",
  });

  const handleRemoveCcEnduser = useCallback(
    (enduserId: string) => {
      const updatedCcEndusers = ccEndusers.filter((enduser) => enduser.id !== enduserId);
      setCcEndusers(updatedCcEndusers);
      setSearchParams((prev) => ({
        ...prev,
        emailCompositionCcIds: updatedCcEndusers.map((e) => e.id),
      }));
    },
    [ccEndusers, setSearchParams]
  );

  const ccEmailAddresses = useMemo(
    () =>
      ccEndusers.reduce<string[]>((acc, e) => {
        if (e.email) {
          acc.push(e.email);
        }
        return acc;
      }, []),
    [ccEndusers]
  );

  if (searchParams.forward && searchParams.emailId && emailToForwardQuery.isLoading) {
    return (
      <div className="flex flex-col h-full items-center justify-center text-muted-foreground gap-2">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        {t("Loading...")}
      </div>
    );
  }

  return (
    <div className="relative flex flex-col h-full">
      {/* Loading Overlay */}
      {isSending && (
        <div className="absolute inset-0 bg-background/50 backdrop-blur-[2px] z-50 flex items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <div className="flex flex-col items-center gap-1">
              <p className="text-muted-foreground">{t("Sending...")}</p>
            </div>
          </div>
        </div>
      )}

      <div className={`flex items-center justify-between px-4 py-2 border-b h-[${PAGE_HEADER_SIZE_STRING}]`}>
        <h3 className="text-lg font-semibold">{t("New Email")}</h3>
        <Link to="..">
          <Button variant="ghost" size="icon">
            <X />
          </Button>
        </Link>
      </div>
      <div className="flex-1 overflow-y-auto">
        <div className="h-full space-y-4 flex flex-col justify-between">
          <div className="flex flex-col gap-4 w-full p-4">
            {/* To - Enduser Selector */}
            <div className="space-y-2">
              <Label htmlFor="recipient">{t("To")}</Label>
              <Combobox
                value={{
                  value: form.getFieldProps("enduserId").value,
                  label: selectedEnduser ? formatName(selectedEnduser) : "",
                  metadata: { enduserId: form.getFieldProps("enduserId").value },
                }}
                formatSelectedValue={({ label }) => <p>{label}</p>}
                onChange={(newVal) => {
                  if (typeof newVal?.metadata?.enduserId === "string") {
                    const enduser = endusersQuery.data?.find((user) => user.id === newVal.metadata?.enduserId);
                    form.setFieldValue("enduserId", newVal?.metadata?.enduserId);
                    setSearchParams((prev) => ({
                      ...prev,
                      emailCompositionEnduserId: newVal?.metadata?.enduserId ?? "",
                      emailCompositionEmailAddress: enduser?.email ?? undefined,
                    }));
                    setSelectedEnduser(enduser);
                  } else {
                    form.setFieldValue("enduserId", "");
                    form.setFieldValue("emailAddress", "");
                    setSearchParams((prev) => ({
                      ...prev,
                      emailCompositionEnduserId: "",
                      emailCompositionEmailAddress: "",
                    }));
                    setSelectedEnduser(undefined);
                  }
                }}
                formatOption={(props) => (
                  <InboxEmailRecipientItem
                    {...props}
                    enduser={endusersQuery.data?.find((enduser) => enduser.id === props.option.metadata?.enduserId)}
                  />
                )}
                searchQuery={enduserSearch}
                setSearchQuery={setEnduserSearch}
                customFilter={(_, options) => options}
                placeholder={t("Select recipient...")}
                options={
                  endusersQuery.data?.map<ComboboxOption<string>>((enduser) => ({
                    value: enduser.id,
                    label: formatName(enduser),
                    metadata: {
                      enduserId: enduser.id,
                    },
                  })) ?? []
                }
              />
            </div>

            {/* Email Address Selector */}
            {selectedEnduser && availableEmails.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="emailAddress">{t("Email Address")}</Label>
                <Combobox
                  value={{
                    value: form.getFieldProps("emailAddress").value,
                    label:
                      availableEmails.find((e) => e.value === form.getFieldProps("emailAddress").value)?.label ?? "",
                    metadata: { emailAddress: form.getFieldProps("emailAddress").value },
                  }}
                  formatSelectedValue={({ label }) => <p>{label}</p>}
                  onChange={(newVal) => {
                    if (typeof newVal?.metadata?.emailAddress === "string") {
                      form.setFieldValue("emailAddress", newVal.metadata.emailAddress);
                      setSearchParams((prev) => ({
                        ...prev,
                        emailCompositionEmailAddress: newVal.metadata?.emailAddress ?? "",
                      }));
                    }
                  }}
                  placeholder={t("Select email address...")}
                  options={
                    availableEmails.map<ComboboxOption<string>>((email) => ({
                      value: email.value,
                      label: email.label,
                      metadata: {
                        emailAddress: email.value,
                      },
                    })) ?? []
                  }
                />
              </div>
            )}

            {/* CC Selector */}
            <div className="space-y-2">
              <Label htmlFor="cc">{t("CC")}</Label>
              <Combobox
                value={undefined}
                formatSelectedValue={() => null}
                onChange={(newVal) => {
                  if (typeof newVal?.metadata?.enduserId === "string") {
                    const enduser = ccEndusersQuery.data?.endusers.find(
                      (user) => user.id === newVal.metadata?.enduserId
                    );
                    if (enduser && !ccEndusers.find((e) => e.id === enduser.id)) {
                      const updatedCcEndusers = [...ccEndusers, enduser];
                      setCcEndusers(updatedCcEndusers);
                      setSearchParams((prev) => ({
                        ...prev,
                        emailCompositionCcIds: updatedCcEndusers.map((e) => e.id),
                      }));
                    }
                  }
                }}
                formatOption={(props) => (
                  <InboxEmailRecipientItem
                    {...props}
                    enduser={ccEndusersQuery.data?.endusers?.find(
                      (enduser) => enduser.id === props.option.metadata?.enduserId
                    )}
                    relationshipType={props.option.metadata?.relationshipType}
                  />
                )}
                searchQuery={ccEnduserSearch}
                setSearchQuery={setCcEnduserSearch}
                customFilter={(_, options) => options}
                placeholder={t("Add CC recipients...")}
                options={ccEndusersQuery.data?.comboboxOptions ?? []}
              />

              {/* CC Recipients Chips */}
              {ccEndusers.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {ccEndusers.map((enduser) => (
                    <Badge key={enduser.id} variant="secondary" className="gap-1">
                      {formatName(enduser)}
                      <button
                        type="button"
                        onClick={() => handleRemoveCcEnduser(enduser.id)}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label htmlFor="subject">{t("Subject")}</Label>
              <Input
                id="subject"
                type="text"
                placeholder={t("Enter subject")}
                {...form.getFieldProps("subject")}
                onChange={(e) => {
                  form.setFieldValue("subject", e.target.value);
                  setSearchParams((prev) => ({
                    ...prev,
                    emailCompositionSubject: e.target.value,
                  }));
                }}
                className="h-9"
              />
            </div>
          </div>

          <InboxEmailCompositionFooter
            enduserId={form.values.enduserId}
            selectedEmailAddress={
              form.values.emailAddress !== selectedEnduser?.email ? form.values.emailAddress : undefined
            }
            ccEmailAddresses={ccEmailAddresses}
            subject={form.values.subject}
            onSend={(email) => {
              setSearchParams({});
              setIsSending(true);
              form.resetForm();
              if (email.enduserId) {
                pollForThread({ enduserIds: email.enduserId, title: email.subject, type: "Email" });
              }
            }}
            emailIdToForward={searchParams.forward ? searchParams.emailId : undefined}
          />
        </div>
      </div>
    </div>
  );
};
